import React from 'react';

/**
 * CSS personalizzato per prevenire layout shift globalmente
 */
const LayoutStabilityCSS = `
/* Prevenzione Layout Shift Globale */
.prevent-layout-shift {
  /* contain: layout; will-change: contents; */
}

/* Placeholder per testi in caricamento */
.text-loading-placeholder {
  display: inline-block;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading-shimmer 1.5s infinite;
  border-radius: 4px;
  min-height: 1em;
  min-width: 4ch;
}

@keyframes loading-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Dimensioni minime stabili per elementi comuni */
.stable-header-link {
  min-width: 4ch;
  min-height: 1.5rem;
  display: inline-block;
}

.stable-button {
  min-width: 6ch;
  min-height: 2.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.stable-title-h1 {
  min-height: 3rem;
  display: block;
}

.stable-title-h2 {
  min-height: 2.5rem;
  display: block;
}

.stable-title-h3 {
  min-height: 1.75rem;
  display: block;
}

.stable-paragraph {
  min-height: 1.5rem;
  display: block;
}

/* Contenitori stabili */
.stable-container {
  contain: layout style;
  min-height: 1px; /* Forza il contenitore a mantenere altezza */
}

/* Prevenzione flash di contenuto non tradotto */
[data-translation-loading="true"] {
  visibility: hidden;
}

[data-translation-loaded="true"] {
  visibility: visible;
}

/* Skeleton specifici per diversi tipi di contenuto */
.skeleton-text-short { width: 8ch; }
.skeleton-text-medium { width: 12ch; }
.skeleton-text-long { width: 20ch; }
.skeleton-text-full { width: 100%; }

/* CSS Aggressivo per CLS Zero */
section.py-16 {
  min-height: 400px !important;
}

.text-4xl, .text-5xl, .text-6xl {
  min-height: 4rem !important;
  line-height: 1.1 !important;
}

.text-3xl {
  min-height: 3rem !important;
  line-height: 1.2 !important;
}

.text-xl {
  min-height: 2rem !important;
  line-height: 1.3 !important;
}

.grid.grid-cols-1.md\\:grid-cols-3 > div {
  min-height: 280px !important;
}

/* Stabilizza contenitori hero */
.hero-container {
  min-height: 600px !important;
}

/* Stabilizza immagini card */
img[loading="lazy"] {
  aspect-ratio: 1 / 1 !important;
  object-fit: cover !important;
  width: 100% !important;
  height: auto !important;
}

/* Badge con dimensioni fisse */
.absolute.rounded-full {
  min-width: 24px !important;
  min-height: 24px !important;
}

/* Card container fisso */
.rounded-xl.overflow-hidden {
  min-height: 480px !important;
  height: 480px !important;
  contain: layout !important;
}

/* Forza altezza fissa anche per card con border */
.rounded-xl.overflow-hidden.shadow-md {
  height: 480px !important;
  max-height: 480px !important;
}

/* Image container nelle card */
.rounded-xl.overflow-hidden > .relative {
  height: 320px !important;
  max-height: 320px !important;
  flex-shrink: 0 !important;
}

/* Grid stabilizzati */
.grid.grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-3.xl\\:grid-cols-4 {
  min-height: 400px !important;
  contain: layout !important;
}

.grid.grid-cols-1.md\\:grid-cols-3 {
  min-height: 300px !important;
  contain: layout !important;
}
`;

interface LayoutStabilityProviderProps {
  children: React.ReactNode;
}

/**
 * Provider che inietta CSS globale per prevenire layout shift
 */
const LayoutStabilityProvider: React.FC<LayoutStabilityProviderProps> = ({ children }) => {
  React.useEffect(() => {
    // Inietta CSS una sola volta
    const styleId = 'layout-stability-css';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = LayoutStabilityCSS;
      document.head.appendChild(style);
    }

    // Aggiunge classe globale al body per stabilità
    document.body.classList.add('prevent-layout-shift');

    return () => {
      document.body.classList.remove('prevent-layout-shift');
    };
  }, []);

  return <>{children}</>;
};

export default LayoutStabilityProvider;